import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

type UseStickyTimelineScrollbarParams = {
  /** Total scrollable time range. */
  timeStart: number;
  timeEnd: number;
  /** Initial visible window — used to position the thumb before onTimeChange fires. */
  initialVisibleTimeStart: number;
  initialVisibleTimeEnd: number;
};

/**
 * Sticky custom horizontal scrollbar for react-calendar-timeline.
 *
 * Driven by TIME not scrollLeft — RCT re-centres scrollLeft on every pan so it
 * never maps to a real position within [timeStart, timeEnd].
 *
 * Scrubbing converts the time delta to a scrollLeft delta on the RCT element,
 * triggering onTimeChange → updateScrollCanvas naturally.
 *
 * track/thumb use useState (not useRef) so useEffect re-runs when the divs
 * mount — they are conditionally rendered and useRef.current doesn't trigger effects.
 */
export const useStickyTimelineScrollbar = ({ timeStart, timeEnd, initialVisibleTimeStart, initialVisibleTimeEnd }: UseStickyTimelineScrollbarParams) => {
  // useState callback-ref pattern so useEffect re-runs when these mount/unmount.
  const [trackEl, stickyScrollbarRef] = useState<HTMLDivElement | null>(null);
  const [thumbEl, stickyScrollbarThumbRef] = useState<HTMLDivElement | null>(null);
  const [rctScrollEl, scrollRef] = useState<HTMLElement | null>(null);

  const visibleRef = useRef({ start: initialVisibleTimeStart, end: initialVisibleTimeEnd });
  const rangeRef = useRef({ timeStart, timeEnd });
  rangeRef.current = { timeStart, timeEnd };

  const positionThumb = useCallback(
    (vStart: number, vEnd: number) => {
      if (!trackEl || !thumbEl) return;

      const { timeStart: start, timeEnd: end } = rangeRef.current;
      const totalRange = end - start;
      const windowSize = vEnd - vStart;
      const trackWidth = trackEl.clientWidth;
      if (!Number.isFinite(totalRange) || totalRange <= 0 || windowSize <= 0 || trackWidth <= 0) return;

      const thumbWidth = Math.max(24, Math.min(1, windowSize / totalRange) * trackWidth);
      const maxThumbLeft = trackWidth - thumbWidth;
      const scrollableTime = totalRange - windowSize;
      const fraction = scrollableTime > 0 ? Math.min(1, Math.max(0, (vStart - start) / scrollableTime)) : 0;

      thumbEl.style.width = `${thumbWidth}px`;
      thumbEl.style.transform = `translateX(${fraction * maxThumbLeft}px)`;
    },
    [trackEl, thumbEl]
  );

  // Called by the component from onTimeChange — pure DOM write, no React state.
  const syncThumb = useCallback(
    (visibleStart: number, visibleEnd: number) => {
      visibleRef.current = { start: visibleStart, end: visibleEnd };
      positionThumb(visibleStart, visibleEnd);
    },
    [positionThumb]
  );

  // Re-position when elements mount, range changes, or items finish loading.
  useLayoutEffect(() => {
    positionThumb(visibleRef.current.start, visibleRef.current.end);
  }, [positionThumb, timeStart, timeEnd]);

  useEffect(() => {
    if (!rctScrollEl || !trackEl || !thumbEl) return undefined;

    let isDragging = false;
    let didDrag = false;
    let dragStartX = 0;
    let dragStartVisibleStart = 0;

    const getMetrics = () => {
      const { timeStart: start, timeEnd: end } = rangeRef.current;
      const { start: vStart, end: vEnd } = visibleRef.current;
      const totalRange = end - start;
      const windowSize = vEnd - vStart;
      const trackWidth = trackEl.clientWidth;
      const valid = Number.isFinite(totalRange) && totalRange > 0;
      const thumbWidth = valid ? Math.max(24, Math.min(1, windowSize / totalRange) * trackWidth) : trackWidth;
      const maxThumbLeft = trackWidth - thumbWidth;
      const scrollableTime = valid ? Math.max(0, totalRange - windowSize) : 0;
      return { windowSize, maxThumbLeft, scrollableTime };
    };

    const scrub = (rawVisibleStart: number) => {
      const { timeStart: start, timeEnd: end } = rangeRef.current;
      const { start: vStart, end: vEnd } = visibleRef.current;
      const windowSize = vEnd - vStart;
      const clampedStart = Math.max(start, Math.min(end - windowSize, rawVisibleStart));

      visibleRef.current = { start: clampedStart, end: clampedStart + windowSize };
      positionThumb(clampedStart, clampedStart + windowSize);

      if (rctScrollEl.clientWidth > 0) {
        const pixelsPerMs = rctScrollEl.clientWidth / windowSize;
        rctScrollEl.scrollLeft += (clampedStart - vStart) * pixelsPerMs;
      }

      return clampedStart;
    };

    const onThumbMouseDown = (e: MouseEvent) => {
      isDragging = true;
      didDrag = false;
      dragStartX = e.clientX;
      dragStartVisibleStart = visibleRef.current.start;
      e.preventDefault();
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      didDrag = true;
      const { maxThumbLeft, scrollableTime } = getMetrics();
      if (maxThumbLeft <= 0 || scrollableTime <= 0) return;
      const dx = e.clientX - dragStartX;
      const rawStart = dragStartVisibleStart + (dx / maxThumbLeft) * scrollableTime;
      const clamped = scrub(rawStart);
      if (clamped !== rawStart) {
        dragStartX = e.clientX;
        dragStartVisibleStart = clamped;
      }
    };

    const onMouseUp = () => {
      isDragging = false;
    };

    const onTrackClick = (e: MouseEvent) => {
      if (e.target === thumbEl || didDrag) {
        didDrag = false;
        return;
      }
      const { maxThumbLeft, scrollableTime } = getMetrics();
      if (maxThumbLeft <= 0 || scrollableTime <= 0) return;
      const thumbWidth = trackEl.clientWidth - maxThumbLeft;
      const clickX = e.clientX - trackEl.getBoundingClientRect().left - thumbWidth / 2;
      const fraction = Math.max(0, Math.min(1, clickX / maxThumbLeft));
      scrub(rangeRef.current.timeStart + fraction * scrollableTime);
    };

    thumbEl.addEventListener("mousedown", onThumbMouseDown);
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    trackEl.addEventListener("click", onTrackClick);

    const resizeObserver = new ResizeObserver(() => {
      positionThumb(visibleRef.current.start, visibleRef.current.end);
    });
    resizeObserver.observe(trackEl);

    return () => {
      thumbEl.removeEventListener("mousedown", onThumbMouseDown);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      trackEl.removeEventListener("click", onTrackClick);
      resizeObserver.disconnect();
    };
  }, [rctScrollEl, trackEl, thumbEl, positionThumb]);

  return { stickyScrollbarRef, stickyScrollbarThumbRef, scrollRef, syncThumb };
};
